<template>
  <div class="folder-list p-2">
    <div
      v-for="item in items"
      :key="item?._key ?? (item?.is_child ? `child-${item?.id}-${item?.['Application No']}` : `app-${item?.['Application No']}`)"
      class="folder-item"
    >
      <div class="folder-header d-flex justify-content-between align-items-center border table-border-color shadow"
        role="button"
        tabindex="0"
        :aria-expanded="string(!!item?.expanded)"
        @click="ensure_children_loaded(item)"
        @keydown.enter.prevent="ensure_children_loaded(item)"
        @keydown.space.prevent="ensure_children_loaded(item)"
      >
        <div class="d-flex align-items-center gap-2">
          <div class="folder-icon-wrapper">
           <i class="bi bi-folder-fill text-dark-blue ms-2 me-2 fs-2"></i>
          </div>

          <div class="p-2">
            <p class="mb-0 text-xs">
              <span class="fw-bold mt-1">Name : </span>{{ item?.['Entity Name'] }}
            </p>
            <p class="mb-0 text-xs">
              <span class="fw-bold">Entity Type : </span>{{ item?.['Entity Type'] }}
            </p>

            <div v-if="(item?._attributes || []).length" class="mt-1">
              <div class="form-control text-field rounded-lg p-2 bg-gray border text-xxs w-100">
                <div class="attributes-preview border-0 d-flex flex-wrap gap-2">
                  <div
                    v-for="attr in visible_attributes(item)"
                    :key="`preview-${item?._key}-attr-${attr?.entity_type_attribute_id || attr?.attribute_id}`"
                    class="attribute-preview"
                  >
                    <span class="attribute-preview-name">{{ attr?.attribute_name }}:</span>
                    <span class="attribute-preview-value">
                      <template v-if="is_file(attr)">
                        <i v-if="file_href(attr)" class="fa-regular fa-file text-success"></i>
                        <i v-else class="fa-regular fa-file text-muted"></i>
                      </template>
                      <template v-else>
                        {{ has_value(attr) ? truncate_text(as_text(attr), 20) : '—' }}
                      </template>
                    </span>
                  </div>

                  <div v-if="(item?._attributes || []).length > 3" class="more-attributes text-muted text-x">
                    ...
                  </div>
                </div>
              </div>
            </div>

            <small class="text-muted text-xs">{{ item?.['Date Created'] }}</small>
          </div>
        </div>

        <div class="actions d-flex align-items-center">
          <button class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark me-2" @click.stop="on_view(item)">
            View
          </button>

          <template v-if="get_item_status(item) === 'In_Progress'">
            <button
              v-if="!item.is_child && !custom_actions"
              class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark me-2"
              @click.stop="on_move_to_master(item)"
              :disabled="moving_to_master_loading"
            >
              <span v-if="moving_to_master_loading">
                <i class="fa-solid fa-spinner fa-spin"></i> Moving...
              </span>
              <span v-else>
                Move to Master
              </span>
            </button>
          </template>

          <template v-else-if="get_item_status(item) === 'Submitted_to_Master'">
            <button
              class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark me-2"
              @click="$emit('submit-to-masters-office', item)"
            >
              Submit to Master's Office
            </button>
          </template>

          <template v-else-if="get_item_status(item) === 'Submitted_to_Masters_Office'">
            <button
              class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark me-2"
              @click="$emit('upload-letter', item)"
            >
              Upload Letter
            </button>
            <button
              class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark me-2"
              @click="$emit('complete-application', item)"
            >
              Complete Application
            </button>
            <button
              class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark me-2"
              @click="$emit('export-contract', item)"
            >
              Export Contract
            </button>
          </template>

          <template v-else>
            <button
              v-if="!custom_actions"
              class="d-none btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark me-2"
              @click.stop="on_add_sub(item)"
            >
              Add Sub-Entity
            </button>
          </template>

          <button
            class=" text-x text-dark me-2 border-0 text-decoration-none bg-transparent"
            @click.stop="on_toggle(item)"
            :disabled="!!item?.is_loading"
            aria-label="Toggle children"
          >
            <i v-if="item?.is_loading" class="fa fa-spinner fa-spin"></i>
            <i v-else :class="item?.expanded ? 'fa-solid fa-angle-down fs-4' : 'fa-solid fa-angle-right fs-4'"></i>
          </button>
        </div>
      </div>

      <div v-if="item?.expanded" class="children-container">

        <!-- Show children when present -->
        <div v-if="(item?.children || []).length" class="hierarchy-line">
          <FolderList
            :items="item?.children || []"
            @view="$emit('view', $event)"
            @add-sub="$emit('add-sub', $event)"
            @fetch-assigned="$emit('fetch-assigned', $event)"
            @move-to-master="$emit('move-to-master', $event)"
            @submit-to-masters-office="$emit('submit-to-masters-office', $event)"
            @upload-letter="$emit('upload-letter', $event)"
            @complete-application="$emit('complete-application', $event)"
            @export-contract="$emit('export-contract', $event)"
            :moving_to_master_loading="moving_to_master_loading"
            :custom-actions="custom_actions"
          />
        </div>

        <div v-else class="empty-children mx-5 m-0">
          <div class="alert alert-info text-center  rounded-3 shadow-sm empty-children-card w-100">
            <div class="mb-1">
              <i class="fa-solid fa-circle-info me-1" aria-hidden="true"></i>
              <span class="fw-semibold">No sub-entities found</span>
            </div>
            <div class="text-xxs text-muted">
              There are no sub-entities under this parent entity yet.<br />
              Please ensure you've added sub-entities under this entity.
            </div>
          </div>
        </div>

      </div>

    </div>

    <div v-if="!items || !items.length" class="text-muted text-xxs ps-2">No items to display.</div>
  </div>
</template>

<script>
export default {
  name: "FolderList",
  props: {
    items: { type: Array, required: true },
    moving_to_master_loading: { type: Boolean, default: false },
    custom_actions: { type: Boolean, default: false }
  },
  methods: {
    get_item_status(item) {
      return item.Status || item.status || item._raw?.status?.status || '';
    },

    on_view(item) {  
      const application_id = Number(item["Application No"]);
      const entity_type_id = Number(item.entity_type_id);
      const entity_profile_id = item.is_child
        ? Number(item.id)
        : (item.entity_profile_id != null ? Number(item.entity_profile_id) : null);
      const entity_name = item['Entity Name'] || '';
      const status = item._child_raw?.forms?.[0]?.status || item.Status || '';
      
      // Extract user_id from multiple possible sources
      let user_id = null;
      
      const user_source = [
        item._child_raw?.profile_role?.[0]?.user_id
      ];
      
      for (const source of user_source) {
        if (source && source !== "N/A" && !isNaN(Number(source))) {
          user_id = Number(source);
          break;
        }
      }
      

      this.$emit("view", {
        application_id,
        entity_profile_id,
        entity_type_id,
        entity_name,
        status,
        user_id: user_id || undefined
      });
    },

    on_move_to_master(item) {
      this.$emit("move-to-master", item);
    },

    on_toggle(item) {
      if (!item.expanded) {
         if (!item._children_loaded) {
          this.$emit('fetch-assigned', item);
           return;
         }
         item.expanded = true;
         return;
      }
      item.expanded = false;
    },

    on_add_sub(item) {
      this.$emit("add-sub", item);
    },


    visible_attributes(item) {
      const arr = Array.isArray(item._attributes) ? item._attributes : [];
      return arr.slice(0, 3);
    },

    truncate_text(text, max_length) {
      if (!text) return '';
      return text.length > max_length ? text.substring(0, max_length) + '...' : text;
    },

    is_file(attr) {
      return (attr?.attribute_type || '').toLowerCase() === 'file';
    },

    has_value(attr) {
      if (typeof attr?.has_value === 'boolean') return attr.has_value;
      return attr?.value != null && String(attr.value).trim() !== '';
    },

    file_href(attr) {
      const v = attr?.value;
      if (!v) return '';
      if (typeof v === 'string') return v;
      if (v && typeof v === 'object' && typeof v.url === 'string') return v.url;
      return '';
    },

    as_text(attr) {
      const v = attr?.value;
      if (v == null) return '';
      if (typeof v === 'string') return v;
      try { return JSON.stringify(v); } catch { return String(v); }
    },

    ensure_children_loaded(item) {
      if (!item) return;

      if (!item._children_checked && !item.is_loading) {
        this.$emit('fetch-assigned', item);
        item.expanded = true;
        return;
      }

      if (!item.is_loading) {
        item.expanded = !item.expanded;
      }
    },

    is_collapsible() {
      return true;
    },

    has_visible_children(item) {
      return !!(item && Array.isArray(item.children) && item.children.length && item.expanded);
    },

    show_hint(item) {
      if (!item) return false;
      if (!item._children_checked && !item.is_loading) return true;
      return !!(item._children_checked && item.has_children && !item.expanded);
    },

    aria_expanded(item) {
      return this.is_collapsible(item) ? String(!!item.expanded) : null;
    },

    string(value) {
      return String(value);
    }
  }
};
</script>

<style scoped>
.text-field {
  min-height: 10px;
  min-width:100px;
  background-color: #f8f9fa;
  overflow-y: auto;
  resize: none;
  display: block;
  width: 100%;
}

.folder-item {
  margin-bottom: 1rem;
}

.folder-header {
  background:white!important;
  border-radius: 8px;
  padding: 0.75rem;
  border: 1px solid #e9ecef;
  transition: all 0.2s ease;
}

.folder-header:hover {
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.actions button {
  margin-left: 0.5rem;
}

.folder-icon-wrapper {
  position: relative;
}

.file-link {
  color: var(--dark-blue, #1a365d);
  text-decoration: none;
  font-weight: 500;
}

.file-link:hover {
  text-decoration: underline;
}

.children-container {
  margin-top: 0.75rem;
}

.hierarchy-line {
  margin-left: 1.5rem;
  padding-left: 1.5rem;
  border-left: 2px solid #ccc;
  position: relative;
}

.hierarchy-line .folder-item {
  position: relative;
}

.hierarchy-line .folder-item::before {
  content: '';
  position: absolute;
  left: -1.5rem;
  top: 1.5rem;
  width: 1rem;
  height: 2px;
  background-color: #ccc;
}

.hierarchy-line .folder-item:last-child::after {
  content: '';
  position: absolute;
  left: -1.5rem;
  top: 1.5rem;
  width: 2px;
  height: calc(100% - 1.5rem);
  background-color: white;
}

@media (max-width: 768px) {
  .folder-header {
    flex-direction: column;
    align-items: stretch;
  }

  .actions {
    justify-content: flex-start;
    flex-wrap: wrap;
    margin-top: 0.5rem;
  }
}

.folder-header:active {
  transform: scale(0.998);
}
</style>